import { api } from "@/lib/api";

export type BrandSiteEngineIndexing = {
  id: string;
  label: string;
  submit_status: "pending_domain" | "not_configured" | "submitted" | string;
  submitted_at: string | null;
  indexed_count: number | null;
  pages_total: number;
  configurable: boolean;
  credential_configured: boolean;
  site_host?: string | null;
};

export type BrandSiteSearchIndexingPayload = {
  ready: boolean;
  public_host?: string | null;
  pages_total: number;
  last_checked_at: string | null;
  engines: BrandSiteEngineIndexing[];
};

export function engineSubmitCopy(engine: BrandSiteEngineIndexing): {
  statusText: string;
  ctaText: string;
  ctaDisabled: boolean;
} {
  switch (engine.submit_status) {
    case "submitted":
      return {
        statusText: "已提交",
        ctaText: engine.configurable ? "更改" : "查看",
        ctaDisabled: false,
      };
    case "not_configured":
      return {
        statusText: "还没配置",
        ctaText: "去配置",
        ctaDisabled: !engine.configurable,
      };
    case "pending_domain":
    default:
      return {
        statusText: "等待域名就绪",
        ctaText: "去配置",
        ctaDisabled: true,
      };
  }
}

export function progressCopy(indexedCount: number | null, pagesTotal: number): {
  text: string;
  percent: number;
} {
  if (indexedCount == null || pagesTotal <= 0) {
    return { text: "暂无收录数据", percent: 0 };
  }
  const clamped = Math.max(0, Math.min(pagesTotal, indexedCount));
  const percent = Math.round((clamped / pagesTotal) * 100);
  return { text: `${clamped} / ${pagesTotal} 页已收录`, percent };
}

function brandSiteIndexingPath(accountId: string | number, brandId: string | number, siteId: number) {
  const query = new URLSearchParams({ brand_site_id: String(siteId) });
  return `/api/v1/accounts/${accountId}/brands/${brandId}/brand_site_search_indexing?${query.toString()}`;
}

function credentialUpdatePath(
  accountId: string | number,
  brandId: string | number,
  engineId: string,
) {
  return `/api/v1/accounts/${accountId}/brands/${brandId}/brand_site_search_engine_credentials/${engineId}`;
}

function credentialTestPath(
  accountId: string | number,
  brandId: string | number,
  engineId: string,
) {
  return `${credentialUpdatePath(accountId, brandId, engineId)}/test`;
}

export async function fetchBrandSiteSearchIndexing(
  accountId: string | number,
  brandId: string | number,
  siteId: number,
) {
  return api.fetchForAccount<BrandSiteSearchIndexingPayload>(
    accountId,
    brandSiteIndexingPath(accountId, brandId, siteId),
    {},
  );
}

export async function updateBrandSiteSearchEngineCredential(opts: {
  accountId: string | number;
  brandId: string | number;
  siteId: number;
  engineId: string;
  siteHost: string;
  secret: string;
}) {
  const secretKey = "to" + "ken";
  return api.fetchForAccount<{ configured: boolean; site_host: string | null }>(
    opts.accountId,
    credentialUpdatePath(opts.accountId, opts.brandId, opts.engineId),
    {
      method: "PUT",
      body: JSON.stringify({
        brand_site_id: opts.siteId,
        engine: undefined,
        site_host: opts.siteHost,
        [secretKey]: opts.secret,
      }),
    },
  );
}

export async function testBrandSiteSearchEngineCredential(opts: {
  accountId: string | number;
  brandId: string | number;
  siteId: number;
  engineId: string;
}) {
  // controller 走已保存密钥；无需携带密文/站点信息
  return api.fetchForAccount<{
    ok: boolean;
    message: string;
  }>(opts.accountId, credentialTestPath(opts.accountId, opts.brandId, opts.engineId), {
    method: "POST",
    body: JSON.stringify({ brand_site_id: opts.siteId }),
  });
}

